import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import GetUserCertificates from "../../actions/GetUserCertificates";
import { Award, Download, ExternalLink, Calendar, ShieldCheck } from "lucide-react";

const NAVY = "#16335c";
const GOLD = "#c9a227";
const ORANGE_GRAD = "linear-gradient(135deg, #fb8c00, #f4511e)";

// Downloads a remote file; falls back to opening it in a new tab.
const downloadFile = async (url, filename) => {
  try {
    const r = await fetch(url, { mode: "cors" });
    const blob = await r.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(u), 1000);
  } catch (e) {
    window.open(url, "_blank");
  }
};

const downloadSvg = (svg, filename) => {
  const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(u), 1000);
};

// Open a certificate in a new tab (image url, or an svg blob url).
const openCert = (c) => {
  if (c.image_url) {
    window.open(c.image_url, "_blank", "noopener,noreferrer");
  } else if (c.svg) {
    const u = URL.createObjectURL(new Blob([c.svg], { type: "image/svg+xml;charset=utf-8" }));
    window.open(u, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(u), 30000);
  }
};

// Friendly label, e.g. "siliconminds-internship" / "skp-internship" -> "Silicon Minds · Internship".
const typeLabel = (t) => {
  const map = {
    "siliconminds-internship": "Internship",
    "siliconminds-training": "Training",
    "siliconminds-workshop": "Workshop",
    "siliconminds-appreciation": "Appreciation",
    "skp-internship": "Internship",
    internship: "Internship",
    training: "Training",
    workshop: "Workshop",
    appreciation: "Appreciation",
  };
  const key = (t || "").toLowerCase();
  let nice = map[key];
  if (!nice) {
    const base = key.replace(/^siliconminds-/, "").replace(/^skp-/, "");
    nice = base ? base.charAt(0).toUpperCase() + base.slice(1) : "Certificate";
  }
  return "Silicon Minds · " + nice;
};

const prettyDate = (d) => {
  if (!d) return "";
  const dt = new Date(String(d).replace(" ", "T"));
  if (isNaN(dt.getTime())) return String(d).split(" ")[0];
  return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

// Shows the logged-in user's issued certificates. Pass `courseUrl` to limit
// to a single course (e.g. on the course dashboard).
const IssuedCertificates = ({ account, courseUrl, title = "Issued Certificates" }) => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [certs, setCerts] = useState([]);
  const [hover, setHover] = useState(-1);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const session = (user && user.SessionId) || (account && account.SessionId);
        const res = await GetUserCertificates({
          payload: { session, user_id: account && (account.user_id || account.id), course_url: courseUrl },
        });
        if (active && res && res.status === "success") setCerts(res.certificates || []);
      } catch (e) {
        /* ignore — render empty state */
      }
      if (active) setLoading(false);
    };
    load();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseUrl]);

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        padding: 20,
        boxShadow: "0 1px 3px rgba(16,33,60,.08), 0 8px 24px rgba(16,33,60,.06)",
        border: "1px solid #eef1f6",
      }}
    >
      {/* scoped styles for hover / clamp */}
      <style>{`
        .smc-card{transition:transform .18s ease, box-shadow .18s ease;}
        .smc-card:hover{transform:translateY(-4px);}
        .smc-clamp{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
        .smc-btn{transition:background .15s ease, color .15s ease, border-color .15s ease;}
      `}</style>

      {/* header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center" style={{ gap: 12 }}>
          <div
            style={{
              width: 42, height: 42, borderRadius: 12, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: ORANGE_GRAD,
              boxShadow: "0 4px 12px rgba(245,124,0,.3)",
            }}
          >
            <Award size={22} color="#fff" />
          </div>
          <div>
            <h5 className="mb-0" style={{ fontWeight: 700, color: NAVY }}>{title}</h5>
            <span style={{ fontSize: 12.5, color: "#7a8699" }}>Your verified achievements</span>
          </div>
        </div>
        {certs.length > 0 && (
          <span
            style={{
              fontSize: 13, fontWeight: 600, color: NAVY,
              background: "#eef3fb", borderRadius: 999, padding: "4px 12px",
            }}
          >
            {certs.length} earned
          </span>
        )}
      </div>

      {/* states */}
      {loading ? (
        <div className="row g-3">
          {[0, 1].map((i) => (
            <div className="col-12 col-md-6" key={i}>
              <div style={{ borderRadius: 14, border: "1px solid #eef1f6", overflow: "hidden" }}>
                <div style={{ aspectRatio: "10 / 7", background: "linear-gradient(100deg,#f3f5f9,#e9edf4,#f3f5f9)" }} />
                <div style={{ padding: 14 }}>
                  <div style={{ height: 12, width: "70%", background: "#eef1f6", borderRadius: 6, marginBottom: 8 }} />
                  <div style={{ height: 10, width: "40%", background: "#f0f2f7", borderRadius: 6 }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : certs.length === 0 ? (
        <div
          style={{
            textAlign: "center", padding: "38px 16px", borderRadius: 14,
            border: "1.5px dashed #d8deea", background: "#fafbfd",
          }}
        >
          <div
            style={{
              width: 56, height: 56, borderRadius: "50%", margin: "0 auto 12px",
              display: "flex", alignItems: "center", justifyContent: "center", background: "#eef3fb",
            }}
          >
            <Award size={26} color={NAVY} />
          </div>
          <p className="mb-1" style={{ fontWeight: 600, color: NAVY }}>No certificate has been issued to you.</p>
          <p className="mb-0" style={{ fontSize: 13, color: "#8a93a5" }}>
            Complete your course to earn your Silicon Minds certificate.
          </p>
        </div>
      ) : (
        <div className="row g-3">
          {certs.map((c, i) => (
            <div className="col-12 col-md-6 col-xl-4" key={(c.certificate_id || "") + i}>
              <div
                className="smc-card h-100"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(-1)}
                style={{
                  borderRadius: 14,
                  border: "1px solid #e7ebf3",
                  overflow: "hidden",
                  background: "#fff",
                  boxShadow: hover === i ? "0 14px 30px rgba(16,33,60,.16)" : "0 2px 8px rgba(16,33,60,.06)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* preview frame */}
                <div
                  style={{
                    position: "relative",
                    aspectRatio: "10 / 7",
                    background: "linear-gradient(135deg,#f7f9fc,#eef2f8)",
                    borderBottom: "1px solid #eef1f6",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: 14, overflow: "hidden",
                  }}
                >
                  {/* verified seal */}
                  <div
                    style={{
                      position: "absolute", top: 10, right: 10, zIndex: 2,
                      display: "flex", alignItems: "center", gap: 5,
                      background: "rgba(255,255,255,.92)", borderRadius: 999,
                      padding: "3px 9px", boxShadow: "0 2px 6px rgba(16,33,60,.12)",
                    }}
                  >
                    <ShieldCheck size={13} color={GOLD} />
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: NAVY, letterSpacing: .3 }}>VERIFIED</span>
                  </div>

                  {c.image_url ? (
                    <img
                      src={c.image_url}
                      alt={c.course_title || "Certificate"}
                      style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 4, boxShadow: "0 6px 18px rgba(16,33,60,.18)" }}
                    />
                  ) : c.svg ? (
                    <div
                      style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
                      dangerouslySetInnerHTML={{ __html: c.svg }}
                    />
                  ) : (
                    <Award size={48} color="#c2cbdb" />
                  )}
                </div>

                {/* body */}
                <div style={{ padding: 14, display: "flex", flexDirection: "column", flex: 1 }}>
                  <p className="smc-clamp mb-2" style={{ fontWeight: 700, color: NAVY, fontSize: 15, lineHeight: 1.3, minHeight: 38 }}>
                    {c.course_title || c.course_url || "Course Certificate"}
                  </p>

                  <div className="d-flex align-items-center justify-content-between mb-3" style={{ gap: 8 }}>
                    <span
                      style={{
                        fontSize: 11.5, fontWeight: 700, color: "#7a5e00",
                        background: "linear-gradient(135deg,#fff5d6,#ffe9a8)",
                        border: `1px solid ${GOLD}55`, borderRadius: 999, padding: "3px 10px", whiteSpace: "nowrap",
                      }}
                    >
                      {typeLabel(c.type)}
                    </span>
                    {c.issued_date && (
                      <span className="d-flex align-items-center" style={{ fontSize: 12, color: "#8a93a5", gap: 4, whiteSpace: "nowrap" }}>
                        <Calendar size={12} /> {prettyDate(c.issued_date)}
                      </span>
                    )}
                  </div>

                  <div className="d-flex mt-auto" style={{ gap: 8 }}>
                    <button
                      className="smc-btn"
                      onClick={() => openCert(c)}
                      style={{
                        flex: 1, border: `1px solid ${NAVY}33`, color: NAVY, background: "#fff",
                        borderRadius: 9, padding: "8px 10px", fontSize: 13, fontWeight: 600,
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6, cursor: "pointer",
                      }}
                    >
                      <ExternalLink size={15} /> View
                    </button>
                    <button
                      className="smc-btn"
                      onClick={() =>
                        c.image_url
                          ? downloadFile(c.image_url, `certificate_${c.certificate_id || i}.png`)
                          : downloadSvg(c.svg || "", `certificate_${c.certificate_id || i}.svg`)
                      }
                      style={{
                        flex: 1, border: "none", color: "#fff",
                        background: ORANGE_GRAD,
                        borderRadius: 9, padding: "8px 10px", fontSize: 13, fontWeight: 600,
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6, cursor: "pointer",
                      }}
                    >
                      <Download size={15} /> Download
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default IssuedCertificates;
